import React, { useState, useEffect, useRef } from 'react';
import { FaLock, FaMinusCircle } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import ShadedSelect from '../ui/ShadedSelect';
import {
    DSE_CORE_SUBJECT_CODES,
    DSE_ELECTIVE_SELECTABLE_SUBJECT_CODES,
    DSE_SUBJECT_CODE_SET,
    getSubjectFullName,
    normalizeSubjectCode,
} from '../../utils/dseSubjects';
import {
    JUPAS_HIGH_RISK_ACTION_CLASS,
} from '../../utils/jupasClassificationDisplay';

// Chinese Language — GCSE/IGCSE grades: flip to true when JUPASelect conversion is ready.
// To remove the gate entirely later: set true, delete `guardStripChineseAlternativeScoresFromRows`,
// and delete its invocation in `loadSavedScores` (keep or inline `CHINESE_ALTERNATIVE_SCORES` as needed).
const SHOW_CHINESE_ALTERNATIVE_SCORES = false;
const MAX_ELECTIVE_ROWS = 4;
const EMPTY_SELECT_LABEL = '—';
const LEGACY_CORE_SCORE_KEYS = new Set([
    'Chinese Language',
    'English Language',
    'Mathematics Compulsory Part',
    'Core Subject',
    'Core Subject Code',
    'Core Score',
    'CSD',
]);
const CORE_SUBJECT_CODE_SET = new Set(DSE_CORE_SUBJECT_CODES);

function isCoreScoreKey(key) {
    const code = normalizeSubjectCode(key);
    return LEGACY_CORE_SCORE_KEYS.has(key) || CORE_SUBJECT_CODE_SET.has(code);
}

function capComponentElectiveRows(rows, maxElectives = MAX_ELECTIVE_ROWS) {
    const coreRows = rows.filter((row) => row.isCompulsory || row.isCore);
    const electiveRows = rows.filter((row) => !row.isCompulsory && !row.isCore);
    const filledElectives = electiveRows.filter((row) => row.subjectCode);
    const emptyElectives = electiveRows.filter((row) => !row.subjectCode);
    const cappedFilled = filledElectives.slice(0, maxElectives);
    const nextRows = [...coreRows, ...cappedFilled];

    if (cappedFilled.length < maxElectives && emptyElectives.length > 0) {
        nextRows.push(emptyElectives[0]);
    } else if (cappedFilled.length === 0) {
        nextRows.push({ subjectCode: '', subject: '', score: '', isCompulsory: false });
    }

    return nextRows;
}
const SCORE_ROW_GRID_CLASS = 'grid grid-cols-[minmax(0,1fr)_4rem_1.25rem] items-center gap-3';
const BALANCED_SCORE_ROW_GRID_CLASS = 'grid grid-cols-[1.25rem_minmax(0,1fr)_4rem_1.25rem] items-center gap-3';
const SCORE_SELECT_BUTTON_CLASS = '!px-1.5 text-left tabular-nums font-medium';

const CHINESE_ALTERNATIVE_SCORES = [
    'GCSE 9', 'GCSE 8', 'GCSE 7', 'GCSE 6', 'GCSE 5', 'GCSE 4', 'GCSE 3', 'GCSE 2', 'GCSE 1',
    'IGCSE 9', 'IGCSE 8', 'IGCSE 7', 'IGCSE 6', 'IGCSE 5', 'IGCSE 4', 'IGCSE 3', 'IGCSE 2', 'IGCSE 1',
    'GCSE A*', 'GCSE A', 'GCSE B', 'GCSE C', 'GCSE D', 'GCSE E', 'GCSE F', 'GCSE G',
    'IGCSE A*', 'IGCSE A', 'IGCSE B', 'IGCSE C', 'IGCSE D', 'IGCSE E', 'IGCSE F', 'IGCSE G',
];

function guardStripChineseAlternativeScoresFromRows(rows) {
    if (SHOW_CHINESE_ALTERNATIVE_SCORES) {
        return { rows, hadRemoval: false };
    }
    const alt = new Set(CHINESE_ALTERNATIVE_SCORES);
    let hadRemoval = false;
    const next = rows.map((r) => {
        if (r.subjectCode === 'CHI' && r.score != null && r.score !== '' && alt.has(r.score)) {
            hadRemoval = true;
            return { ...r, score: '' };
        }
        return r;
    });
    return { rows: next, hadRemoval };
}

const DSEScoreInputer = ({
    onScoresChange,
    onSaveStatusChange,
    embedded = false,
    demoScores = null,
    /** Prefill rows (e.g. from promo home navigate state). Skips auth load when set. */
    seedScores = null,
    persistSeedScores = false,
    onSeedScoresPersisted,
    surveyCompact = false,
    balancedActionGutter = false,
    showCategoryBCRow = false,
    showSpnBonusRow = false,
    initialSurveyExtras = null,
    onSurveyExtrasChange,
    className = '',
}) => {
    const { t, i18n } = useTranslation();
    const { isAuthenticated } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState('');
    const [autoOpenElectiveSlot, setAutoOpenElectiveSlot] = useState(null);
    const [categoryBCActive, setCategoryBCActive] = useState(initialSurveyExtras?.categoryBCActive ?? false);
    const [categoryBCSubject, setCategoryBCSubject] = useState(initialSurveyExtras?.categoryBCSubject ?? '');
    const [categoryBCScore, setCategoryBCScore] = useState(initialSurveyExtras?.categoryBCScore ?? '');
    const [spnBonusActive, setSpnBonusActive] = useState(initialSurveyExtras?.spnBonusActive ?? false);
    const [spnBonusScheme, setSpnBonusScheme] = useState(initialSurveyExtras?.spnBonusScheme ?? '');
    const [spnBonusScore, setSpnBonusScore] = useState(initialSurveyExtras?.spnBonusScore ?? '');
    useEffect(() => {
        onSurveyExtrasChange?.({
            categoryBCSubject,
            categoryBCScore,
            categoryBCActive,
            spnBonusScheme,
            spnBonusScore,
            spnBonusActive,
        });
    }, [
        categoryBCSubject,
        categoryBCScore,
        categoryBCActive,
        spnBonusScheme,
        spnBonusScore,
        spnBonusActive,
        onSurveyExtrasChange,
    ]);
    useEffect(() => {
        onSaveStatusChange?.({ isLoading, saveStatus });
    }, [isLoading, onSaveStatusChange, saveStatus]);
    const inputerRef = useRef(null);
    const seedAppliedRef = useRef(false);
    const saveScoresRef = useRef(null);

    // Check if translations are ready
    const isTranslationReady = i18n.isInitialized && t('dseSubjects.CHI') !== 'dseSubjects.CHI';

    // Regular DSE scores (reversed order)
    const regularScores = ["5**", "5*", "5", "4", "3", "2", "1"];

    const lockedCSDScore = t('dse.attained');
    const scoreRowGridClass = balancedActionGutter
        ? BALANCED_SCORE_ROW_GRID_CLASS
        : SCORE_ROW_GRID_CLASS;
    const scoreSelectButtonClass = `${SCORE_SELECT_BUTTON_CLASS} ${surveyCompact ? '!h-9 !text-base md:!text-[11px]' : ''}`;
    const subjectSelectButtonClass = surveyCompact
        ? '!h-9 !pl-3 !pr-9 !text-base md:!text-[11px]'
        : '';
    const BalancedActionGutter = () => balancedActionGutter ? <span aria-hidden /> : null;

    const LockedScoreDisplay = ({ value, compact = false, showLock }) => {
        const displayLock = showLock ?? !compact;
        return (
        <div
            className={`relative flex w-full items-center rounded-lg border border-edge-strong bg-surface-muted shadow-sm font-medium text-ink-muted ${
                surveyCompact ? 'h-9 text-base md:text-[11px]' : 'min-h-10 text-sm'
            } ${
                compact ? 'justify-start px-1.5 py-2 text-left tabular-nums' : 'px-3 py-2 text-left'
            } ${surveyCompact ? '!py-0' : ''} ${displayLock ? 'pr-7' : ''}`}
            aria-disabled="true"
        >
            <span className="block truncate">{value}</span>
            {displayLock ? (
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-ink-subtle" aria-hidden>
                    <FaLock className="h-3 w-3" />
                </span>
            ) : null}
        </div>
        );
    };

    // Helper function to get subject code from display name
    const getSubjectCode = (displayName) => {
        const codeMap = {
            [t('dseSubjects.CHI')]: 'CHI',
            [t('dseSubjects.ENG')]: 'ENG',
            [t('dseSubjects.MAT')]: 'MAT',
            [t('dseSubjects.CSD')]: 'CSD',
            [t('dseSubjects.BIO')]: 'BIO',
            [t('dseSubjects.CHE')]: 'CHE',
            [t('dseSubjects.PHY')]: 'PHY',
            [t('dseSubjects.CHL')]: 'CHL',
            [t('dseSubjects.LIE')]: 'LIE',
            [t('dseSubjects.CHH')]: 'CHH',
            [t('dseSubjects.ECO')]: 'ECO',
            [t('dseSubjects.ERS')]: 'ERS',
            [t('dseSubjects.GEO')]: 'GEO',
            [t('dseSubjects.HIS')]: 'HIS',
            [t('dseSubjects.THS')]: 'THS',
            [t('dseSubjects.BAF')]: 'BAF',
            [t('dseSubjects.ACC')]: 'ACC',
            [t('dseSubjects.BUS')]: 'BUS',
            [t('dseSubjects.DAT')]: 'DAT',
            [t('dseSubjects.HSC')]: 'HSC',
            [t('dseSubjects.ICT')]: 'ICT',
            [t('dseSubjects.FST')]: 'FST',
            [t('dseSubjects.FCT')]: 'FCT',
            [t('dseSubjects.MUS')]: 'MUS',
            [t('dseSubjects.VSA')]: 'VSA',
            [t('dseSubjects.PHE')]: 'PHE',
            [t('dseSubjects.ME1')]: 'ME1',
            [t('dseSubjects.ME2')]: 'ME2',
            // Fallback for English names / legacy labels
            'Chinese Language': 'CHI',
            'English Language': 'ENG',
            'Mathematics Compulsory Part': 'MAT',
            'Citizenship and Social Development': 'CSD',
            'Biology': 'BIO',
            'Chemistry': 'CHE',
            'Physics': 'PHY',
            'Chinese Literature': 'CHL',
            'Literature in English': 'LIE',
            'Chinese History': 'CHH',
            'Economics': 'ECO',
            'Ethics and Religious Studies': 'ERS',
            'Geography': 'GEO',
            'History': 'HIS',
            'Tourism and Hospitality Studies': 'THS',
            'Business, Accounting and Financial Studies': 'BAF',
            'Business, Accounting and Financial Studies (Accounting)': 'ACC',
            'Business, Accounting and Financial Studies (Business Management)': 'BUS',
            'Design and Applied Technology': 'DAT',
            'Health Management and Social Care': 'HSC',
            'Information and Communication Technology': 'ICT',
            'Technology and Living (Food Science and Technology)': 'FST',
            'Technology and Living (Fashion, Clothing and Textiles)': 'FCT',
            'Music': 'MUS',
            'Visual Arts': 'VSA',
            'Physical Education': 'PHE',
            'Mathematics Extended Part (Calculus and Statistics) - Module 1': 'ME1',
            'Mathematics Extended Part (Algebra and Calculus) - Module 2': 'ME2'
        };
        return normalizeSubjectCode(codeMap[displayName] || displayName);
    };

    const getSubjectDisplayName = (code) => getSubjectFullName(t, code);

    // Initialize state with compulsory subjects in first 3 rows + CSD default in 4th row + 1 elective row
    const [scores, setScores] = useState([
        { subjectCode: 'ENG', subject: t('dseSubjects.ENG'), score: "", isCompulsory: true },
        { subjectCode: 'CHI', subject: t('dseSubjects.CHI'), score: "", isCompulsory: true },
        { subjectCode: 'MAT', subject: t('dseSubjects.MAT'), score: "", isCompulsory: true },
        { subjectCode: 'CSD', subject: t('dseSubjects.CSD'), score: t('dse.attained'), isCore: true },
        { subjectCode: "", subject: "", score: "", isCompulsory: false }
    ]);

    // Update scores when language changes
    useEffect(() => {
        setScores(prevScores => {
            // Update the display names for all subjects while preserving codes
            return prevScores.map((score, index) => {
                if (score.subjectCode) {
                    return {
                        ...score,
                        subject: getSubjectDisplayName(score.subjectCode),
                        score: score.subjectCode === 'CSD' ? lockedCSDScore : score.score
                    };
                }
                return score;
            });
        });
    }, [t, i18n.language]);

    // Load saved scores when component mounts (if user is authenticated and nothing was pre-seeded)
    useEffect(() => {
        if (demoScores?.length || seedScores?.length) return undefined;
        if (isAuthenticated) {
            loadSavedScores();
        }
        return undefined;
    }, [isAuthenticated, demoScores, seedScores]);

    // Pre-fill from promo demo rows or navigate-state seed scores
    useEffect(() => {
        const sourceScores = demoScores?.length
            ? demoScores
            : (!seedAppliedRef.current && seedScores?.length ? seedScores : null);
        if (!sourceScores || !isTranslationReady) return undefined;

        if (!demoScores?.length && seedScores?.length) {
            seedAppliedRef.current = true;
        }

        const scoreMap = Object.fromEntries(
            sourceScores
                .filter((row) => row?.subjectCode)
                .map((row) => [row.subjectCode, row.score])
        );
        const electiveCodes = [];
        const seenElectiveCodes = new Set(['CHI', 'ENG', 'MAT', 'CSD']);
        sourceScores.forEach((row) => {
            const code = normalizeSubjectCode(row.subjectCode);
            if (!code || seenElectiveCodes.has(code)) return;
            if (['CHI', 'ENG', 'MAT', 'CSD'].includes(code)) return;
            seenElectiveCodes.add(code);
            electiveCodes.push(code);
        });
        const cappedElectiveCodes = electiveCodes.slice(0, MAX_ELECTIVE_ROWS);

        const rows = [
            {
                subjectCode: 'ENG',
                subject: getSubjectDisplayName('ENG'),
                score: scoreMap.ENG || '',
                isCompulsory: true,
            },
            {
                subjectCode: 'CHI',
                subject: getSubjectDisplayName('CHI'),
                score: scoreMap.CHI || '',
                isCompulsory: true,
            },
            {
                subjectCode: 'MAT',
                subject: getSubjectDisplayName('MAT'),
                score: scoreMap.MAT || '',
                isCompulsory: true,
            },
            {
                subjectCode: 'CSD',
                subject: getSubjectDisplayName('CSD'),
                score: lockedCSDScore,
                isCore: true,
            },
            ...cappedElectiveCodes.map((code) => ({
                subjectCode: code,
                subject: getSubjectDisplayName(code),
                score: scoreMap[code] || '',
                isCompulsory: false,
            })),
        ];

        if (cappedElectiveCodes.length === 0) {
            rows.push({ subjectCode: '', subject: '', score: '', isCompulsory: false });
        }

        setScores(rows);
        if (onScoresChange) {
            onScoresChange(rows.filter((score) => score.subjectCode && score.score));
        }
        if (!demoScores?.length && persistSeedScores && isAuthenticated) {
            saveScoresRef.current?.(rows).then((saved) => {
                if (saved) onSeedScoresPersisted?.();
            });
        }
        return undefined;
    }, [demoScores, seedScores, persistSeedScores, onSeedScoresPersisted, isAuthenticated, isTranslationReady, i18n.language]);

    // Load saved scores from backend
    const loadSavedScores = async () => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem('token');

            const response = await fetch('/api/dse-scores/load', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(t('dse.failedToLoadSavedScores'));
            }

            const data = await response.json();
            const savedScores = data.scores;

            if (savedScores && Object.keys(savedScores).length > 0) {
                const convertedScores = capComponentElectiveRows(
                    convertSavedScoresToComponentFormat(savedScores)
                );
                const { rows: guardedScores, hadRemoval } = guardStripChineseAlternativeScoresFromRows(convertedScores);
                setScores(guardedScores);

                if (onScoresChange) {
                    onScoresChange(guardedScores.filter(score => score.subjectCode && score.score));
                }

                if (hadRemoval && isAuthenticated) {
                    await saveScores(guardedScores, { manageLoading: false, quiet: true });
                }
            }
        } catch (error) {
            console.error('Error loading saved scores:', error);
            setSaveStatus(t('dse.failedToLoadSavedScores'));
        } finally {
            setIsLoading(false);
        }
    };

    // Convert saved scores object to component format
    const convertSavedScoresToComponentFormat = (savedScores) => {
        const convertedScores = [
            {
                subjectCode: 'ENG',
                subject: getSubjectDisplayName('ENG'),
                score: savedScores['English Language'] || savedScores.ENG || '',
                isCompulsory: true,
            },
            {
                subjectCode: 'CHI',
                subject: getSubjectDisplayName('CHI'),
                score: savedScores['Chinese Language'] || savedScores.CHI || '',
                isCompulsory: true,
            },
            {
                subjectCode: 'MAT',
                subject: getSubjectDisplayName('MAT'),
                score: savedScores['Mathematics Compulsory Part'] || savedScores.MAT || '',
                isCompulsory: true,
            },
            { subjectCode: 'CSD', subject: getSubjectDisplayName('CSD'), score: lockedCSDScore, isCore: true },
        ];

        const electiveEntries = Object.entries(savedScores).filter(
            ([key, value]) => value && !isCoreScoreKey(key)
        );

        const seenElectiveCodes = new Set();

        electiveEntries.forEach(([subjectKey, score]) => {
            if (seenElectiveCodes.size >= MAX_ELECTIVE_ROWS) return;
            if (!subjectKey || !score) return;

            let subjectCode = normalizeSubjectCode(subjectKey);
            let subjectName = subjectKey;

            if (subjectKey.length > 3 && !subjectKey.includes('_')) {
                subjectCode = normalizeSubjectCode(getSubjectCode(subjectKey));
                subjectName = getSubjectDisplayName(subjectCode);
            } else {
                subjectName = getSubjectDisplayName(subjectCode);
            }

            if (!DSE_SUBJECT_CODE_SET.has(subjectCode)) return;
            if (CORE_SUBJECT_CODE_SET.has(subjectCode)) return;
            if (seenElectiveCodes.has(subjectCode)) return;

            seenElectiveCodes.add(subjectCode);
            convertedScores.push({
                subjectCode,
                subject: subjectName,
                score,
                isCompulsory: false,
            });
        });

        if (seenElectiveCodes.size === 0) {
            convertedScores.push({ subjectCode: '', subject: '', score: '', isCompulsory: false });
        }

        return convertedScores;
    };

    // Convert component format to saved scores object
    const convertComponentFormatToSavedScores = (componentScores) => {
        const savedScores = {};

        componentScores.forEach((row, index) => {
            if (row.subjectCode && row.score) {
                let translatedScore = row.subjectCode === 'CSD' ? 'Attained' : row.score;

                if (row.isCompulsory) {
                    // Map subject codes to backend keys for compulsory subjects
                    if (row.subjectCode === 'CHI') {
                        savedScores["Chinese Language"] = translatedScore;
                    } else if (row.subjectCode === 'ENG') {
                        savedScores["English Language"] = translatedScore;
                    } else if (row.subjectCode === 'MAT') {
                        savedScores["Mathematics Compulsory Part"] = translatedScore;
                    } else {
                        savedScores[row.subjectCode] = translatedScore;
                    }
                } else if (row.isCore) {
                    savedScores["Core Subject Code"] = row.subjectCode;
                    savedScores["Core Score"] = translatedScore;
                } else {
                    savedScores[row.subjectCode] = translatedScore;
                }
            }
        });

        return savedScores;
    };

    // Save scores to backend
    const saveScores = async (scoresToSave, { manageLoading = true, quiet = false } = {}) => {
        if (!isAuthenticated) return;

        try {
            if (manageLoading) setIsLoading(true);
            const token = localStorage.getItem('token');
            const scoresObject = convertComponentFormatToSavedScores(
                capComponentElectiveRows(scoresToSave)
            );

            const response = await fetch('/api/dse-scores/save', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ scores: scoresObject })
            });

            if (!response.ok) {
                throw new Error(t('dse.failedToSaveScores'));
            }

            if (!quiet) {
                setSaveStatus(t('dse.scoresSavedAutomatically'));
                setTimeout(() => setSaveStatus(''), 3000);
            }
            return true;
        } catch (error) {
            console.error('Error saving scores:', error);
            if (!quiet) {
                setSaveStatus(t('dse.failedToSaveScores'));
            }
            return false;
        } finally {
            if (manageLoading) setIsLoading(false);
        }
    };
    saveScoresRef.current = saveScores;

    // Handle subject selection (for core and elective subjects)
    const handleSubjectChange = (index, subjectCode) => {
        const code = normalizeSubjectCode(subjectCode);
        if (
            code
            && scores.some((row, rowIndex) => rowIndex !== index && normalizeSubjectCode(row.subjectCode) === code)
        ) {
            return;
        }
        const previousRow = scores[index];
        const previousScore = previousRow?.score ?? '';
        const availableScores = getAvailableScores(code);
        let nextScore = code === 'CSD' ? lockedCSDScore : previousScore;
        if (nextScore && !availableScores.includes(nextScore)) {
            nextScore = '';
        }
        const newScores = [...scores];
        newScores[index] = {
            subjectCode: code,
            subject: getSubjectDisplayName(code),
            score: nextScore,
            isCompulsory: false,
            isCore: false
        };
        setScores(newScores);

        // Notify parent component
        if (onScoresChange) {
            onScoresChange(newScores.filter(score => score.subjectCode && score.score));
        }

        // Auto-save if user is authenticated
        if (isAuthenticated) {
            saveScores(newScores);
        }
    };

    // Handle score selection
    const handleScoreChange = (index, score) => {
        const newScores = [...scores];
        newScores[index] = {
            ...newScores[index],
            score: score
        };
        setScores(newScores);

        // Notify parent component
        if (onScoresChange) {
            onScoresChange(newScores.filter(score => score.subjectCode && score.score));
        }

        // Auto-save if user is authenticated
        if (isAuthenticated) {
            saveScores(newScores);
        }
    };

    // Add a new elective row
    const addRow = (slotIndex) => {
        if (!canAddRow) return;
        setAutoOpenElectiveSlot(slotIndex);
        setScores((currentScores) => {
            const electiveCount = currentScores.filter(
                (row) => !row.isCompulsory && !row.isCore
            ).length;
            if (electiveCount >= MAX_ELECTIVE_ROWS) return currentScores;
            return [...currentScores, { subjectCode: "", subject: "", score: "", isCompulsory: false }];
        });
    };

    // Remove a row (only elective rows can be removed)
    const removeRow = (index) => {
        if (scores.length > 4 && !scores[index].isCompulsory && !scores[index].isCore) {
            const newScores = scores.filter((_, i) => i !== index);
            setScores(newScores);

            // Notify parent component
            if (onScoresChange) {
                onScoresChange(newScores.filter(score => score.subjectCode && score.score));
            }

            // Auto-save if user is authenticated
            if (isAuthenticated) {
                saveScores(newScores);
            }
        }
    };

    // Get available scores based on selected subject
    const getAvailableScores = (subjectCode) => {
        if (subjectCode === 'CSD') return [lockedCSDScore];
        if (subjectCode === 'CHI') {
            return SHOW_CHINESE_ALTERNATIVE_SCORES
                ? [...regularScores, ...CHINESE_ALTERNATIVE_SCORES]
                : regularScores;
        }
        return regularScores;
    };

    const electiveRows = scores
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => !row.isCompulsory && !row.isCore);
    const canAddRow = electiveRows.length < MAX_ELECTIVE_ROWS;
    const electiveSlots = Array.from(
        { length: MAX_ELECTIVE_ROWS },
        (_, slotIndex) => electiveRows[slotIndex] || null
    );

    // Get available subjects for a row, excluding subjects already selected elsewhere.
    const getAvailableSubjects = (rowIndex) => {
        const selectedCodes = new Set(
            scores
                .map((row, index) => (
                    index !== rowIndex && row.subjectCode
                        ? normalizeSubjectCode(row.subjectCode)
                        : null
                ))
                .filter(Boolean)
        );

        return DSE_ELECTIVE_SELECTABLE_SUBJECT_CODES
            .filter((code) => !selectedCodes.has(code))
            .map((code) => ({
                code,
                name: getSubjectDisplayName(code),
            }));
    };

    return (
        <div ref={inputerRef} className={`dse-score-inputer mx-auto w-full max-w-xl ${className}${embedded ? ' dse-score-inputer--embedded' : ''}`} key={i18n.language}>
            <div className={`${surveyCompact ? 'space-y-1' : 'space-y-2'} ${embedded ? 'text-xs' : ''}`}>
                {scores.slice(0, 4).map((row, index) => (
                    <div key={row.subjectCode} className={scoreRowGridClass}>
                        <BalancedActionGutter />
                        <LockedScoreDisplay value={row.subject} />
                        {row.subjectCode === 'CSD' ? <LockedScoreDisplay value={lockedCSDScore} compact showLock /> : (
                            <ShadedSelect value={row.score} onChange={(v) => handleScoreChange(index, v)} size={embedded ? 'sm' : 'md'} hideChevron={embedded} buttonClassName={scoreSelectButtonClass} options={[
                                { value: '', label: EMPTY_SELECT_LABEL },
                                ...getAvailableScores(row.subjectCode).map((score) => ({ value: score, label: score })),
                            ]} />
                        )}
                        <span aria-hidden />
                    </div>
                ))}
                {electiveSlots.map((entry, slotIndex) => {
                    if (!entry) {
                        return (
                            <div key={`empty-elective-${slotIndex}`} className={`${scoreRowGridClass} opacity-70`}>
                                <BalancedActionGutter />
                                <button type="button" onClick={() => addRow(slotIndex)} disabled={!canAddRow} className={`dse-elective-slot-activator flex w-full items-center rounded-lg border border-edge-strong bg-surface-muted px-3 text-left text-ink-subtle transition hover:bg-edge/70 disabled:cursor-not-allowed ${surveyCompact ? 'h-9 py-0' : 'min-h-10 py-2'}`} aria-label={t('dse.addAnotherElectiveSubject')} title={t('dse.addAnotherElectiveSubject')}>
                                    <span className={`truncate font-medium ${surveyCompact ? 'text-base md:text-[11px]' : 'text-sm'}`}>{t('dse.selectSubject')}</span>
                                </button>
                                <LockedScoreDisplay value={EMPTY_SELECT_LABEL} compact />
                                <span aria-hidden />
                            </div>
                        );
                    }
                    const { row, index } = entry;
                    return (
                        <div key={index} className={scoreRowGridClass}>
                            <BalancedActionGutter />
                            <ShadedSelect
                                value={row.subjectCode}
                                onChange={(v) => handleSubjectChange(index, v)}
                                className="min-w-0 w-full"
                                size={embedded ? 'sm' : 'md'}
                                buttonClassName={subjectSelectButtonClass}
                                autoOpen={autoOpenElectiveSlot === slotIndex}
                                onAutoOpen={() => setAutoOpenElectiveSlot(null)}
                                options={[
                                { value: '', label: EMPTY_SELECT_LABEL },
                                ...getAvailableSubjects(index).map((subject) => ({ value: subject.code, label: subject.name })),
                            ]}
                            />
                            {row.subjectCode === 'CSD' ? <LockedScoreDisplay value={lockedCSDScore} compact showLock /> : (
                                <ShadedSelect value={row.score} onChange={(v) => handleScoreChange(index, v)} disabled={!row.subjectCode} size={embedded ? 'sm' : 'md'} hideChevron={embedded} buttonClassName={scoreSelectButtonClass} options={[
                                    { value: '', label: EMPTY_SELECT_LABEL },
                                    ...(row.subjectCode ? getAvailableScores(row.subjectCode).map((score) => ({ value: score, label: score })) : []),
                                ]} />
                            )}
                            <button
                                type="button"
                                onClick={() => electiveRows.length > 1 && removeRow(index)}
                                disabled={electiveRows.length <= 1}
                                className={`dse-elective-action-button jupas-action-button group -mx-1.5 inline-flex h-8 w-8 shrink-0 items-center justify-center p-0 ${electiveRows.length > 1 ? JUPAS_HIGH_RISK_ACTION_CLASS : 'cursor-not-allowed text-ink-subtle/30'}`}
                                aria-label={t('dse.remove')}
                                title={t('dse.remove')}
                            >
                                <span className={`inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors ${electiveRows.length > 1 ? 'group-hover:bg-red-50 group-active:bg-red-100' : ''}`}>
                                    <FaMinusCircle className="h-4 w-4" aria-hidden />
                                </span>
                            </button>
                        </div>
                    );
                })}
                {showCategoryBCRow ? categoryBCActive ? (
                    <div className={scoreRowGridClass}>
                        <BalancedActionGutter />
                        <input
                            type="text"
                            name="categoryBCSubject"
                            value={categoryBCSubject}
                            onChange={(event) => setCategoryBCSubject(event.target.value)}
                            placeholder={t('jupasListSubmission.categoryBCSubject')}
                            aria-label={t('jupasListSubmission.categoryBCSubject')}
                            autoComplete="off"
                            autoFocus
                            className="h-9 min-w-0 w-full rounded-lg border border-edge-strong bg-surface-muted px-3 text-base font-medium text-ink shadow-sm outline-none transition placeholder:text-ink-subtle focus:border-primary focus:ring-2 focus:ring-ring md:text-[11px]"
                        />
                        <input
                            type="text"
                            name="categoryBCScore"
                            value={categoryBCScore}
                            onChange={(event) => setCategoryBCScore(event.target.value)}
                            placeholder={t('jupasListSubmission.categoryBCScore')}
                            aria-label={t('jupasListSubmission.categoryBCScore')}
                            autoComplete="off"
                            className="h-9 min-w-0 w-full rounded-lg border border-edge-strong bg-surface-muted px-1.5 text-base font-medium tabular-nums text-ink shadow-sm outline-none transition placeholder:text-ink-subtle focus:border-primary focus:ring-2 focus:ring-ring md:text-[11px]"
                        />
                        <button
                            type="button"
                            onClick={() => {
                                setCategoryBCSubject('');
                                setCategoryBCScore('');
                                setCategoryBCActive(false);
                            }}
                            className={`dse-elective-action-button jupas-action-button group -mx-1.5 inline-flex h-8 w-8 shrink-0 items-center justify-center p-0 ${JUPAS_HIGH_RISK_ACTION_CLASS}`}
                            aria-label={t('jupasListSubmission.removeCategoryBCSubject')}
                            title={t('jupasListSubmission.removeCategoryBCSubject')}
                        >
                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors group-hover:bg-red-50 group-active:bg-red-100">
                                <FaMinusCircle className="h-4 w-4" aria-hidden />
                            </span>
                        </button>
                    </div>
                ) : (
                    <div className={`${scoreRowGridClass} opacity-70`}>
                        <BalancedActionGutter />
                        <button
                            type="button"
                            onClick={() => setCategoryBCActive(true)}
                            className="flex h-9 w-full items-center rounded-lg border border-edge-strong bg-surface-muted px-3 py-0 text-left text-ink-subtle transition hover:bg-edge/70"
                            aria-label={t('jupasListSubmission.addCategoryBCSubject')}
                            title={t('jupasListSubmission.addCategoryBCSubject')}
                        >
                            <span className="truncate text-base font-medium md:text-[11px]">
                                {t('jupasListSubmission.categoryBCPrompt')}
                            </span>
                        </button>
                        <LockedScoreDisplay value={t('jupasListSubmission.categoryBCScore')} compact />
                        <span aria-hidden />
                    </div>
                ) : null}
                {showSpnBonusRow ? spnBonusActive ? (
                    <div className={scoreRowGridClass}>
                        <BalancedActionGutter />
                        <input
                            type="text"
                            name="spnBonusScheme"
                            value={spnBonusScheme}
                            onChange={(event) => setSpnBonusScheme(event.target.value)}
                            placeholder={t('jupasListSubmission.spnBonusScheme')}
                            aria-label={t('jupasListSubmission.spnBonusScheme')}
                            autoComplete="off"
                            autoFocus
                            className="h-9 min-w-0 w-full rounded-lg border border-edge-strong bg-surface-muted px-3 text-base font-medium text-ink shadow-sm outline-none transition placeholder:text-ink-subtle focus:border-primary focus:ring-2 focus:ring-ring md:text-[11px]"
                        />
                        <input
                            type="text"
                            name="spnBonusScore"
                            value={spnBonusScore}
                            onChange={(event) => setSpnBonusScore(event.target.value)}
                            placeholder={t('jupasListSubmission.spnBonusScore')}
                            aria-label={t('jupasListSubmission.spnBonusScore')}
                            autoComplete="off"
                            className="h-9 min-w-0 w-full rounded-lg border border-edge-strong bg-surface-muted px-1.5 text-base font-medium tabular-nums text-ink shadow-sm outline-none transition placeholder:text-ink-subtle focus:border-primary focus:ring-2 focus:ring-ring md:text-[11px]"
                        />
                        <button
                            type="button"
                            onClick={() => {
                                setSpnBonusScheme('');
                                setSpnBonusScore('');
                                setSpnBonusActive(false);
                            }}
                            className={`dse-elective-action-button jupas-action-button group -mx-1.5 inline-flex h-8 w-8 shrink-0 items-center justify-center p-0 ${JUPAS_HIGH_RISK_ACTION_CLASS}`}
                            aria-label={t('jupasListSubmission.removeSpnBonus')}
                            title={t('jupasListSubmission.removeSpnBonus')}
                        >
                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors group-hover:bg-red-50 group-active:bg-red-100">
                                <FaMinusCircle className="h-4 w-4" aria-hidden />
                            </span>
                        </button>
                    </div>
                ) : (
                    <div className={`${scoreRowGridClass} opacity-70`}>
                        <BalancedActionGutter />
                        <button
                            type="button"
                            onClick={() => setSpnBonusActive(true)}
                            className="flex h-9 w-full items-center rounded-lg border border-edge-strong bg-surface-muted px-3 py-0 text-left text-ink-subtle transition hover:bg-edge/70"
                            aria-label={t('jupasListSubmission.addSpnBonus')}
                            title={t('jupasListSubmission.addSpnBonus')}
                        >
                            <span className="truncate text-base font-medium md:text-[11px]">
                                {t('jupasListSubmission.spnBonusPrompt')}
                            </span>
                        </button>
                        <LockedScoreDisplay value={t('jupasListSubmission.spnBonusScore')} compact />
                        <span aria-hidden />
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default DSEScoreInputer; 
